import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Pencil, X } from 'lucide-react'
import { Button, Input } from '@ludus/ui'
import type { Manager } from '@ludus/core'
import { fullName } from '../utils/managerName.ts'

interface NameEditorProps {
  manager: Manager
  disabled: boolean
  onSave: (name: { firstName: string; lastName: string }) => Promise<boolean>
}

export function NameEditor({ manager, disabled, onSave }: NameEditorProps) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [firstName, setFirstName] = useState(manager.firstName)
  const [lastName, setLastName] = useState(manager.lastName)
  const empty = firstName.trim().length === 0

  const open = () => {
    setFirstName(manager.firstName)
    setLastName(manager.lastName)
    setEditing(true)
  }
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (empty) return
    if (await onSave({ firstName: firstName.trim(), lastName: lastName.trim() })) setEditing(false)
  }

  if (!editing) {
    return (
      <div className="flex min-w-0 items-center gap-2">
        <h2 className="truncate text-base font-semibold text-foreground">{fullName(manager)}</h2>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0 text-muted"
          disabled={disabled}
          aria-label={t('pages.managers.name.edit')}
          title={t('pages.managers.name.edit')}
          onClick={open}
        >
          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      </div>
    )
  }

  return (
    <form className="flex flex-wrap items-center gap-2" onSubmit={submit}>
      <Input
        autoFocus
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        onKeyDown={(e) => e.key === 'Escape' && setEditing(false)}
        aria-label={t('pages.managers.name.first')}
        aria-invalid={empty}
        placeholder={t('pages.managers.name.first')}
        className="h-8 w-40"
      />
      <Input
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
        onKeyDown={(e) => e.key === 'Escape' && setEditing(false)}
        aria-label={t('pages.managers.name.last')}
        placeholder={t('pages.managers.name.last')}
        className="h-8 w-40"
      />
      <Button type="submit" size="sm" disabled={disabled || empty}>
        <Check className="h-3.5 w-3.5" aria-hidden="true" />
        {t('pages.managers.name.save')}
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)}>
        <X className="h-3.5 w-3.5" aria-hidden="true" />
        {t('pages.managers.name.cancel')}
      </Button>
      {empty && (
        <span role="alert" className="basis-full text-[11px] font-medium text-toast-error-text">
          {t('pages.managers.name.required')}
        </span>
      )}
    </form>
  )
}
